import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Smartphone, Bell, History, Search, Send, Apple, Loader2 } from 'lucide-react';
import api from '../utils/api';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All patients' },
  { value: 'has_app', label: 'Downloaded app' },
  { value: 'active_7d', label: 'Active in 7 days' },
  { value: 'active_30d', label: 'Active in 30 days' },
  { value: 'inactive_30d', label: 'Inactive 30+ days' },
  { value: 'never', label: 'Never opened' },
];

const ACTIVITY_BADGES = {
  active_7d: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  active_30d: 'bg-blue-100 text-blue-700 border-blue-200',
  inactive_30d: 'bg-amber-100 text-amber-700 border-amber-200',
  never: 'bg-slate-100 text-slate-500 border-slate-200',
};

function formatRelativeDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const days = Math.floor(diffMs / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  } catch { return iso; }
}

function StatTile({ label, value, accent }) {
  return (
    <Card className={`p-4 rounded-2xl border ${accent || 'border-slate-200'} bg-white`}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-1" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value ?? '—'}</p>
    </Card>
  );
}

export default function AppUsersPage({ user, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [status, setStatus] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('last_active_desc');
  const [page, setPage] = useState(1);
  const limit = 25;

  // Selection
  const [selected, setSelected] = useState(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);

  // Send modal
  const [sendOpen, setSendOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [screen, setScreen] = useState('');
  const [sending, setSending] = useState(false);

  // History modal
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit, sort });
      if (status !== 'all') params.append('status', status);
      if (platform !== 'all') params.append('platform', platform);
      if (search.trim()) params.append('search', search.trim());
      const res = await api.get(`/admin/patients/app-usage?${params.toString()}`);
      setStats(res.data.stats || {});
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Could not load app users');
    } finally {
      setLoading(false);
    }
  }, [status, platform, search, sort, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleRow = (pid) => {
    const s = new Set(selected);
    if (s.has(pid)) s.delete(pid); else s.add(pid);
    setSelected(s);
    setSelectAllMatching(false);
  };

  const toggleAllOnPage = () => {
    if (items.every((i) => selected.has(i.patient_id))) {
      const s = new Set(selected);
      items.forEach((i) => s.delete(i.patient_id));
      setSelected(s);
    } else {
      const s = new Set(selected);
      items.forEach((i) => s.add(i.patient_id));
      setSelected(s);
    }
    setSelectAllMatching(false);
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        body: body.trim(),
        data: screen.trim() ? { screen: screen.trim() } : {},
      };
      if (selectAllMatching) {
        payload.filter = {};
        if (status !== 'all') payload.filter.status = status;
        if (platform !== 'all') payload.filter.platform = platform;
        if (search.trim()) payload.filter.search = search.trim();
        if (!Object.keys(payload.filter).length) payload.filter = { status: 'has_app' };
      } else {
        payload.patient_ids = Array.from(selected);
        if (!payload.patient_ids.length) {
          toast.error('Select at least one patient');
          setSending(false);
          return;
        }
      }
      const res = await api.post('/admin/notifications/send', payload);
      toast.success(`Sent to ${res.data.sent} • Failed ${res.data.failed}`);
      setSendOpen(false);
      setTitle(''); setBody(''); setScreen('');
      setSelected(new Set()); setSelectAllMatching(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/admin/notifications/history?limit=50');
      setHistory(res.data.items || []);
    } catch { toast.error('Could not load history'); }
    finally { setHistoryLoading(false); }
  };

  const openHistory = () => { setHistoryOpen(true); loadHistory(); };

  const selectionCount = selectAllMatching ? total : selected.size;
  const allOnPageSelected = items.length > 0 && items.every((i) => selected.has(i.patient_id));

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <header className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Smartphone className="w-6 h-6 text-indigo-600" /> App Users
            </h1>
            <p className="text-slate-500 text-sm mt-1">See who's installed the patient mobile app and send push notifications.</p>
          </div>
          <Button variant="outline" className="rounded-xl" onClick={openHistory} data-testid="app-users-history-btn">
            <History className="w-4 h-4 mr-2" /> Notification history
          </Button>
        </header>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          <StatTile label="Total Patients" value={stats.total_patients} />
          <StatTile label="Downloaded" value={stats.downloaded} accent="border-indigo-200" />
          <StatTile label="Active 7d" value={stats.active_7d} accent="border-emerald-200" />
          <StatTile label="Active 30d" value={stats.active_30d} accent="border-blue-200" />
          <StatTile label="Inactive 30d+" value={stats.inactive_30d} accent="border-amber-200" />
          <StatTile label="Push Reachable" value={stats.push_reachable} accent="border-purple-200" />
          <StatTile label="Total Sessions" value={stats.total_sessions} accent="border-pink-200" />
        </div>

        {/* Filter bar */}
        <Card className="p-4 rounded-2xl border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <Label className="text-xs">Search (name or phone)</Label>
              <div className="relative mt-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Type to filter…"
                  className="pl-9 rounded-xl"
                  data-testid="app-users-search-input"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">Status</Label>
              <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="app-users-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Platform</Label>
              <Select value={platform} onValueChange={(v) => { setPlatform(v); setPage(1); }}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="app-users-platform-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="ios">iOS</SelectItem>
                  <SelectItem value="android">Android</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">Sort</Label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_active_desc">Last active (newest)</SelectItem>
                  <SelectItem value="first_seen_desc">First seen (newest)</SelectItem>
                  <SelectItem value="name_asc">Name (A–Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Bulk action bar */}
        {(selectionCount > 0 || allOnPageSelected) && (
          <Card className="p-3 rounded-2xl bg-indigo-50 border-indigo-200 flex items-center justify-between flex-wrap gap-3" data-testid="app-users-bulk-bar">
            <div className="text-sm text-slate-700 flex items-center gap-2">
              <strong>{selectAllMatching ? `All ${total}` : selectionCount} patients selected</strong>
              {allOnPageSelected && !selectAllMatching && total > items.length && (
                <button
                  className="text-indigo-600 underline text-xs"
                  onClick={() => setSelectAllMatching(true)}
                  data-testid="app-users-select-all-matching"
                >
                  Select all {total} matching the filter
                </button>
              )}
              {selectAllMatching && (
                <button className="text-slate-500 underline text-xs" onClick={() => { setSelectAllMatching(false); setSelected(new Set()); }}>
                  Clear selection
                </button>
              )}
            </div>
            <Button onClick={() => setSendOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 rounded-xl" data-testid="app-users-send-btn">
              <Send className="w-4 h-4 mr-2" /> Send notification
            </Button>
          </Card>
        )}

        {/* Table */}
        <Card className="rounded-2xl border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <Checkbox checked={allOnPageSelected} onCheckedChange={toggleAllOnPage} data-testid="app-users-select-all-page" />
                  </th>
                  <th className="px-4 py-3 text-left">Patient</th>
                  <th className="px-4 py-3 text-left">Phone</th>
                  <th className="px-4 py-3 text-left">Platform</th>
                  <th className="px-4 py-3 text-left">Version</th>
                  <th className="px-4 py-3 text-left">First seen</th>
                  <th className="px-4 py-3 text-left">Last active</th>
                  <th className="px-4 py-3 text-left">Push</th>
                  <th className="px-4 py-3 text-left">Sessions</th>
                  <th className="px-4 py-3 text-left">Devices</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={10} className="px-4 py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">No patients match the filter</td></tr>
                ) : items.map((it) => (
                  <tr key={it.patient_id} className="hover:bg-slate-50" data-testid={`app-users-row-${it.patient_id}`}>
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selected.has(it.patient_id)}
                        onCheckedChange={() => toggleRow(it.patient_id)}
                        data-testid={`app-users-select-${it.patient_id}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">{it.name || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{it.phone}</td>
                    <td className="px-4 py-3">
                      {it.platform === 'ios' && <span className="inline-flex items-center gap-1 text-slate-700"><Apple className="w-3.5 h-3.5" /> iOS</span>}
                      {it.platform === 'android' && <span className="text-emerald-700">Android</span>}
                      {!it.platform && <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{it.app_version || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{formatRelativeDate(it.first_seen_at)}</td>
                    <td className="px-4 py-3">
                      <Badge className={`border text-xs ${ACTIVITY_BADGES[it.activity_status] || ''}`}>{formatRelativeDate(it.last_active_at)}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      {it.push_enabled ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-xs">ON</Badge>
                        : <Badge className="bg-slate-100 text-slate-500 border-slate-200 border text-xs">OFF</Badge>}
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-medium" data-testid={`app-users-sessions-${it.patient_id}`}>{it.total_sessions ?? 0}</td>
                    <td className="px-4 py-3 text-slate-600">{it.device_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pager */}
          <div className="flex items-center justify-between p-3 text-sm text-slate-600">
            <span>{total ? `${(page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total}` : '0'}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-xl" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button>
              <Button variant="outline" size="sm" className="rounded-xl" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>Next</Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Send modal */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="rounded-2xl" data-testid="app-users-send-dialog">
          <DialogHeader>
            <DialogTitle>Send push notification</DialogTitle>
            <DialogDescription>
              Sending to {selectAllMatching ? `all ${total} patients matching the filter` : `${selected.size} selected patient(s)`}. Only patients with push enabled will receive it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Title <span className="text-rose-500">*</span></Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 60))} placeholder="e.g. Your follow-up is due" className="rounded-xl mt-1" data-testid="app-users-send-title" maxLength={60} />
              <p className="text-xs text-slate-400 mt-1">{title.length}/60</p>
            </div>
            <div>
              <Label className="text-sm">Message <span className="text-rose-500">*</span></Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value.slice(0, 240))} placeholder="Short, friendly message. Avoid sharing any medical info." className="rounded-xl mt-1" rows={3} data-testid="app-users-send-body" maxLength={240} />
              <p className="text-xs text-slate-400 mt-1">{body.length}/240</p>
            </div>
            <div>
              <Label className="text-sm">Deep link screen (optional)</Label>
              <Input value={screen} onChange={(e) => setScreen(e.target.value)} placeholder="book / appointments / profile" className="rounded-xl mt-1" data-testid="app-users-send-screen" />
              <p className="text-xs text-slate-400 mt-1">Tapping the notification opens this screen in the app.</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
              ⚠️ Never include diagnosis, treatment notes, or any private medical info. The notification text is visible on the lock screen.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleSend} disabled={sending} className="bg-indigo-600 hover:bg-indigo-700 rounded-xl text-white" data-testid="app-users-send-confirm">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History modal */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="rounded-2xl max-w-3xl" data-testid="app-users-history-dialog">
          <DialogHeader>
            <DialogTitle>Notification history</DialogTitle>
            <DialogDescription>Past push notifications sent from the admin panel.</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : history.length === 0 ? (
            <p className="py-6 text-center text-slate-400">No notifications sent yet.</p>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {history.map((h) => (
                <Card key={h.id} className="p-3 rounded-xl border-slate-200">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <p className="font-semibold text-slate-900">{h.title}</p>
                      <p className="text-sm text-slate-600">{h.body}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">{new Date(h.created_at).toLocaleString()}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                    <span>By: {h.sent_by_admin_name || '—'}</span>
                    <span>·</span>
                    <span className="text-emerald-600">✓ {h.recipients_delivered} delivered</span>
                    <span>·</span>
                    <span className="text-rose-600">✗ {h.recipients_failed} failed</span>
                    <span>·</span>
                    <span>{h.recipients_total} total</span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
